import {
  Shadow,
  FabricImage,
  Canvas,
  Group,
  type ImageProps,
  type ObjectEvents,
  type SerializedImageProps,
  util,
  StaticCanvas,
} from 'fabric'
import { PLACEMENT_TYPES, DIRECTIONS } from '../constants/positions'
import shadowImages from '../constants/shadowImages'
import { usePlacementStore } from '@/stores/placement'
import rotateShadowImages from '@/constants/rotateShadowImages'
import { v4 as uuidv4 } from 'uuid'
import { useBrandStore } from '@/stores/brand'
import chroma from 'chroma-js'
import { argbFromHex, hexFromArgb, TonalPalette } from '@material/material-color-utilities'
import { layerNames } from '@/constants/layers'
import { useEditorStore } from '@/stores/editor'
import type { AIShadowProperties, GenerateAIShadowsRequest, ShadowProducts } from '@/types/aiShadows'
import CreativeService from '@/services/creativeService'
import { useProductStore } from '@/stores/product'
import type { ProductWithInfo } from '@/types/product'
import { useAppStore } from '@/stores/app'
import { shadowTypes } from '@/constants/shadows'

enum placementTypes {
  Front = 'front',
  Top = 'top',
  Float = 'float',
  Corner = 'corner',
  Collage = 'collage',
  Pyramid = 'pyramid',
  Stack = 'stack',
  Vertical = 'vertical',
}

export const AMBIENT_SHADOW_COLOR = 'rgba(0, 0, 0, 0.4)'
export const DROP_SHADOW_COLOR = 'rgba(0, 0, 0, 0.3)'

const MULTIPLE_PRODUCTS_DROP_SHADOW_COLOR = 'rgba(0, 0, 0, 0.7)'
const MULTIPLE_PRODUCTS_DROP_SHADOW_OFFSET_X = 0
const MULTIPLE_PRODUCTS_DROP_SHADOW_OFFSET_Y = 10
const MULTIPLE_PRODUCTS_DROP_SHADOW_BLUR = 15

export function getBottomPoint(img: any, angle: number): { x: number; y: number } {
  if (!img || !img.getElement || !img.width || !img.height) {
    console.error('Invalid image object')
    return { x: 0, y: 0 }
  }

  const el = img.getElement()
  const width = img.width
  const height = img.height

  // Create offscreen canvas to read image pixels
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = width
  tempCanvas.height = height
  const ctx = tempCanvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get 2D context')
    return { x: 0, y: 0 }
  }
  ctx.drawImage(el, 0, 0)

  const imageData = ctx.getImageData(0, 0, width, height).data

  let bottomY = -1
  let bottomX = 0

  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const alpha = imageData[i + 3]
      if (alpha > 10) {
        bottomY = y
        bottomX = x
        break
      }
    }
    if (bottomY !== -1) break
  }

  if (bottomY === -1) {
    return { x: img.left, y: img.top } // fully transparent
  }

  // Determine origin offset in local image space
  const originX = img.originX || 'left'
  const originY = img.originY || 'top'

  let originOffsetX = 0
  let originOffsetY = 0

  if (originX === 'center') originOffsetX = width / 2
  else if (originX === 'right') originOffsetX = width

  if (originY === 'center') originOffsetY = height / 2
  else if (originY === 'bottom') originOffsetY = height

  // Local position relative to origin
  const dx = (bottomX - originOffsetX) * img.scaleX
  const dy = (bottomY - originOffsetY) * img.scaleY

  // Apply rotation around the origin
  const theta = (angle * Math.PI) / 180
  const rotatedX = dx * Math.cos(theta) - dy * Math.sin(theta)
  const rotatedY = Math.abs(dx * Math.sin(theta)) + dy * Math.cos(theta)

  return {
    x: img.left + rotatedX,
    y: img.top + rotatedY,
  }
}

function getVisibleVerticalBounds(fabricImage: FabricImage) {
  const imgElement = fabricImage.getElement()
  const width = fabricImage.width
  const height = fabricImage.height

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return { left: null, right: null }
  }

  ctx.drawImage(imgElement, 0, 0)
  const data = ctx.getImageData(0, 0, width, height).data

  let top = -1
  let bottom = -1

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3]
      if (alpha > 10) {
        if (top === -1) top = y
        bottom = y
        break
      }
    }
  }

  return { top, bottom }
}

// Main function to get left & right bounds in bottom X% of visible region
function getLeftRightInBottomVisibleRegion(fabricImage: FabricImage, bottomPercent = 0.15) {
  const imgElement = fabricImage.getElement()
  const width = fabricImage.width
  const height = fabricImage.height

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return { left: null, right: null }
  }

  ctx.drawImage(imgElement, 0, 0)
  const data = ctx.getImageData(0, 0, width, height).data

  const { top, bottom } = getVisibleVerticalBounds(fabricImage)
  if (top === -1 || bottom === -1 || top == null || bottom == null)
    return { left: null, right: null }

  const visibleHeight = bottom - top + 1
  const contentBottomY = bottom
  const contentStartY = Math.max(top, Math.floor(contentBottomY - visibleHeight * bottomPercent))

  let leftmost = width
  let rightmost = 0

  for (let y = contentStartY; y <= contentBottomY; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3]
      if (alpha > 10) {
        if (x < leftmost) leftmost = x
        if (x > rightmost) rightmost = x
      }
    }
  }

  if (leftmost > rightmost) return { left: null, right: null }

  return {
    left: leftmost,
    right: rightmost,
    contentStartY,
    contentBottomY,
    visibleHeight,
  }
}

export function analyzeImageShape(image: FabricImage): {
  isBottomCurved: boolean
  isTopWider: boolean
  bottomWidth: number
  topWidth: number
  shapeType: string
} {
  const el = image.getElement()
  const canvas = document.createElement('canvas')
  canvas.width = el.width
  canvas.height = el.height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return {
      isBottomCurved: false,
      isTopWider: false,
      bottomWidth: el.width,
      topWidth: el.width,
      shapeType: 'rectangular',
    }
  }

  ctx.drawImage(el, 0, 0)
  const data = ctx.getImageData(0, 0, el.width, el.height).data

  // Sample multiple horizontal sections to understand the shape
  const sections = []

  for (let section = 0; section < 20; section++) {
    const y = Math.floor((section * el.height) / 20)
    let leftmost = el.width
    let rightmost = 0
    let hasPixels = false

    // Find leftmost and rightmost pixels in this horizontal section
    for (let x = 0; x < el.width; x++) {
      const alpha = data[(y * el.width + x) * 4 + 3]
      if (alpha > 10) {
        hasPixels = true
        leftmost = Math.min(leftmost, x)
        rightmost = Math.max(rightmost, x)
      }
    }

    if (hasPixels) {
      sections.push({
        y,
        width: rightmost - leftmost + 1,
        leftmost,
        rightmost,
      })
    }
  }

  if (sections.length === 0) {
    return {
      isBottomCurved: false,
      isTopWider: false,
      bottomWidth: el.width,
      topWidth: el.width,
      shapeType: 'rectangular',
    }
  }

  // Get top sections (first 25%) and bottom sections (last 5%)
  const topSectionCount = Math.max(1, Math.floor(sections.length * 0.9)) // Top 25%
  const bottomSectionCount = Math.max(1, Math.floor(sections.length * 0.1)) // Bottom 5%

  const topSections = sections.slice(0, topSectionCount)
  const bottomSections = sections.slice(-bottomSectionCount)

  // Calculate average widths
  const topWidth = Math.max(...topSections.map(s => s.width))
  const bottomWidth = Math.max(...bottomSections.map(s => s.width))

  // Calculate if top is wider than bottom
  const isTopWider = topWidth >= bottomWidth // 10% threshold

  // Check for curvature by analyzing width variation in bottom 30%
  const bottomCurveSections = sections.slice(-Math.max(1, Math.floor(sections.length * 0.3))) // Bottom 30%
  let maxWidth = 0
  let minWidth = Infinity

  bottomCurveSections.forEach((section) => {
    maxWidth = Math.max(maxWidth, section.width)
    minWidth = Math.min(minWidth, section.width)
  })

  const widthVariation = maxWidth - minWidth
  const isBottomCurved = widthVariation > 5 // Adjust threshold as needed

  // Determine shape type
  let shapeType = 'rectangular'
  if (isBottomCurved) {
    if (isTopWider) {
      shapeType = 'inverted-curved' // Like bottles, inverted triangles
    } else {
      shapeType = 'curved' // Like bowls, regular curved bottoms
    }
  } else if (isTopWider) {
    shapeType = 'inverted-rectangular' // Like inverted trapezoids
  }

  return {
    isBottomCurved,
    isTopWider,
    bottomWidth: Math.round(bottomWidth),
    topWidth: Math.round(topWidth),
    shapeType,
  }
}

export function isBottomCurved(image: FabricImage): boolean {
  const el = image.getElement()
  const canvas = document.createElement('canvas')
  canvas.width = el.width
  canvas.height = el.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return false

  ctx.drawImage(el, 0, 0)
  const data = ctx.getImageData(0, el.height - 10, el.width, 10).data

  const visiblePixels: number[] = []

  for (let x = 0; x < el.width; x++) {
    for (let y = 9; y >= 0; y--) {
      const idx = (y * el.width + x) * 4 + 3 // alpha channel
      if (data[idx] > 10) {
        visiblePixels.push(10 - y)
        break
      }
    }
  }

  // Check for variance in bottom shape
  const max = Math.max(...visiblePixels)
  const min = Math.min(...visiblePixels)
  const variation = max - min

  return variation > 3 // Adjust threshold
}

function isIrregularShaped(image: FabricImage): boolean {
  const el = image.getElement()
  const canvas = document.createElement('canvas')
  canvas.width = el.width
  canvas.height = el.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return false
  const checkHeight = Math.floor(el.height * 0.98)

  ctx.drawImage(el, 0, 0)
  const data = ctx.getImageData(0, 0, el.width, el.height).data

  const invisiblePixels = []
  for (let x = 0; x < el.width; x++) {
    const idx = (checkHeight * el.width + x) * 4 + 3 // alpha channel
    if (data[idx] < 10) {
      invisiblePixels.push(x)
    }
  }
  let totalVariation = 0
  for (let i = 1; i < invisiblePixels.length; i++) {
    if (Math.abs(invisiblePixels[i] - invisiblePixels[i - 1]) > 4) {
      totalVariation += 1
    }
  }

  return totalVariation >= 2
}

// Helper function to get the actual bottom points in canvas coordinates
export function getBottomCanvasPoints(
  img: FabricImage,
  shape: string,
): {
  leftBottom: { x: number; y: number }
  rightBottom: { x: number; y: number }
  centerBottom: { x: number; y: number }
  leftBottomWithoutRotation: { x: number; y: number }
  rightBottomWithoutRotation: { x: number; y: number }
  centerBottomWithoutRotation: { x: number; y: number }
  rotatedTop?: { x: number; y: number }
} {
  const imgElement = img.getElement()
  const width = img.width
  const height = img.height

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    // Fallback to geometric calculation
    const coords = img.getCoords()
    const bottomY = Math.max(...coords.map((c) => c.y))
    return {
      leftBottom: { x: img.left - img.getScaledWidth() / 2, y: bottomY },
      rightBottom: { x: img.left + img.getScaledWidth() / 2, y: bottomY },
      centerBottom: { x: img.left, y: bottomY },
      leftBottomWithoutRotation: { x: img.left - img.getScaledWidth() / 2, y: bottomY },
      rightBottomWithoutRotation: { x: img.left + img.getScaledWidth() / 2, y: bottomY },
      centerBottomWithoutRotation: { x: img.left, y: bottomY },
    }
  }

  ctx.drawImage(imgElement, 0, 0)
  const imageData = ctx.getImageData(0, 0, width, height).data

  // Check if bottom is curved
  const isCurved = isBottomCurved(img)
  const irregularShaped = isIrregularShaped(img)

  let bottomRow = -1
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) {
      const alpha = imageData[(y * width + x) * 4 + 3]
      if (alpha > 10) {
        bottomRow = y
        break
      }
    }
    if (bottomRow !== -1) break
  }

  // let bottomRowLength = -1
  const bounds = getLeftRightInBottomVisibleRegion(img, 0.05)
  const { left, right } = bounds

  let leftBottomY = -1
  let leftBottomX = -1
  let rightBottomY = -1
  let rightBottomX = -1
  let centerBottomY = -1
  let centerBottomX = -1

  let leftBottomWithoutRotationX = -1,
    leftBottomWithoutRotationY = -1
  let rightBottomWithoutRotationX = -1,
    rightBottomWithoutRotationY = -1

  let shadowWidth = 0
  if (left != null && right != null) {
    shadowWidth = right - left
  }

  if(shape == 'tilted'){
    let rightmostX = -1;
    for (let x = width - 1; x >= 0; x--) {
      for (let y = height - 1; y >= height * 0.95; y--) {
        const alpha = imageData[(y * width + x) * 4 + 3];
        if (alpha > 10) {
          leftBottomX = x;
          leftBottomY = y;
          rightmostX = x;
          rightBottomX = x;
          rightBottomY = y;
          break;
        }
      }
      if (rightmostX !== -1) break;
    }
  }
  else if (isCurved && !irregularShaped && (shape == 'concave' || shape == 'pouch')) {
    // For curved bottoms: find bottom-most points in the width * 0.95 region (outermost 5% on each side)
    const leftRegionStart = left
    const leftRegionEnd = left != null ? Math.floor(left + shadowWidth * 0.01) : 0
    const rightRegionStart = right != null ? Math.floor(right) : 0
    const rightRegionEnd = right != null ? Math.floor(right - shadowWidth * 0.01) : 0

    if (!analyzeImageShape(img).isTopWider) {
      const bounds2 = getLeftRightInBottomVisibleRegion(img, 0.03)
      const { left, right } = bounds2
      const left2 = left,
        right2 = right
      if (left2 != null) {
        for (let y = height - 1; y >= 0; y--) {
          const alpha = imageData[(y * width + left2) * 4 + 3]
          if (alpha > 10) {
            leftBottomY = y
            leftBottomX = left2
            break
          }
        }
      }

      if (right2 != null) {
        for (let y = height - 1; y >= 0; y--) {
          const alpha = imageData[(y * width + right2) * 4 + 3]
          if (alpha > 10) {
            rightBottomY = y
            rightBottomX = right2
            break
          }
        }
      }
    } else {
      // Find bottom-most pixel in left 5% region
      if (leftRegionStart != null && leftRegionEnd != null) {
        for (let y = height - 1; y >= 0; y--) {
          for (let x = leftRegionStart; x < leftRegionEnd; x++) {
            const alpha = imageData[(y * width + x) * 4 + 3]
            if (alpha > 10) {
              if (leftBottomY === -1) {
                leftBottomY = y
              }
              leftBottomX = x
              break
            }
          }
        }
      }

      // Find bottom-most pixel in right 5% region
      for (let y = height - 1; y >= 0; y--) {
        for (let x = rightRegionStart; x >= rightRegionEnd; x--) {
          const alpha = imageData[(y * width + x) * 4 + 3]
          if (alpha > 10) {
            if (rightBottomY === -1) {
              rightBottomY = y
            }
            rightBottomX = x
            break
          }
        }
      }
    }
  } else {
    if (
      Math.abs(img.angle || 0) <= 4 &&
      (shadowWidth ? shadowWidth <= img.getScaledWidth() * 0.1 : false)
    ) {
      if (bottomRow !== -1) {
        const bounds3 = getLeftRightInBottomVisibleRegion(img, 0.01)
        const bottomEnd = bounds3.contentStartY ? bounds3.contentStartY : bottomRow - (bottomRow * 0.05)
        for (let y = bottomRow; y >= bottomEnd; y--) {
          // Find leftmost pixel in the bottom row
          for (let x = 0; x < width; x++) {
            const alpha = imageData[(y * width + x) * 4 + 3]
            if (alpha > 10) {
              if (x < leftBottomX || leftBottomX === -1) {
                leftBottomY = y
                leftBottomX = x
                break
              }
            }
          }

          // Find rightmost pixel in the bottom row
          for (let x = width - 1; x >= 0; x--) {
            const alpha = imageData[(y * width + x) * 4 + 3]
            if (alpha > 10) {
              if (x > rightBottomX || rightBottomX === -1) {
                rightBottomY = y
                rightBottomX = x
                break
              }
            }
          }
        }
      }
    } else {
      // For non-curved bottoms: find the bottom-most row, then get leftmost and rightmost points in that row

      // First, find the bottom-most row that has any visible pixels
      const angle = util.degreesToRadians(img.angle || 0)
      const scaleX = img.scaleX ?? 1
      const scaleY = img.scaleY ?? 1
      const originx = img.originX || 'center'
      const originy = img.originY || 'center'
      const left = img.left ?? 0
      const top = img.top ?? 0

      // Calculate image center offset
      const ox = originx === 'center' ? width / 2 : originx === 'right' ? width : 0
      const oy = originy === 'center' ? height / 2 : originy === 'bottom' ? height : 0

      let bottomPixel: { x: number; y: number } | null = null
      let maxCanvasY = -Infinity

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = imageData[(y * width + x) * 4 + 3]
          if (alpha > 10) {
            // Step 1: move origin to center based on originX/Y
            const dx = (x - ox) * scaleX
            const dy = (y - oy) * scaleY

            // Step 2: rotate the point
            const rx = dx * Math.cos(angle) - dy * Math.sin(angle)
            const ry = dx * Math.sin(angle) + dy * Math.cos(angle)

            // Step 3: translate to canvas coordinates
            const canvasX = left + rx
            const canvasY = top + ry

            if (canvasY > maxCanvasY) {
              maxCanvasY = canvasY
              bottomPixel = { x, y }
            }
          }
        }
      }
      leftBottomX = bottomPixel ? bottomPixel.x : leftBottomX
      leftBottomY = bottomPixel ? bottomPixel.y : leftBottomY
      rightBottomX = bottomPixel ? bottomPixel.x : rightBottomX
      rightBottomY = bottomPixel ? bottomPixel.y : rightBottomY
    }
  }

  // Find center bottom point (overall bottom-most pixel) - same logic for both curved and non-curved
  if (left != null && right != null) {
    const leftRegionStart = Math.floor(left + (right - left) * 0.5)
    const leftRegionEnd = Math.floor(left + (right - left) * 0.7)
    for (let y = height - 1; y >= 0; y--) {
      for (let x = leftRegionStart; x < leftRegionEnd; x++) {
        const alpha = imageData[(y * width + x) * 4 + 3]
        if (alpha > 10) {
          centerBottomY = y
          centerBottomX = x
          break
        }
      }
      if (centerBottomY !== -1) break
    }
  }

  for (let x = Math.floor(centerBottomX - shadowWidth * 0.1); x <= Math.floor(centerBottomX); x--) {
    for (let y = height - 1; y >= 0; y--) {
      const alpha = imageData[(y * width + x) * 4 + 3]
      if (alpha > 10) {
        leftBottomWithoutRotationX = x
        leftBottomWithoutRotationY = y
        break
      }
    }
    if (leftBottomWithoutRotationY !== -1) break
  }

  for (let x = Math.floor(centerBottomX + shadowWidth * 0.5); x >= Math.floor(centerBottomX); x--) {
    for (let y = height - 1; y >= 0; y--) {
      const alpha = imageData[(y * width + x) * 4 + 3]
      if (alpha > 10) {
        rightBottomWithoutRotationX = x
        rightBottomWithoutRotationY = y
        break
      }
    }
    if (rightBottomWithoutRotationY !== -1) break
  }

  // If any bottom point wasn't found, use fallback
  if (leftBottomY === -1 || rightBottomY === -1 || centerBottomY === -1) {
    const coords = img.getCoords()
    const fallbackBottomY = Math.max(...coords.map((c) => c.y))
    return {
      leftBottom: {
        x: img.left - img.getScaledWidth() / 2,
        y: leftBottomY == -1 ? fallbackBottomY : leftBottomY,
      },
      rightBottom: {
        x: img.left + img.getScaledWidth() / 2,
        y: rightBottomY == -1 ? fallbackBottomY : rightBottomY,
      },
      centerBottom: { x: img.left, y: centerBottomY == -1 ? fallbackBottomY : centerBottomY },
      leftBottomWithoutRotation: {
        x: img.left - img.getScaledWidth() / 2,
        y: leftBottomY == -1 ? fallbackBottomY : leftBottomY,
      },
      rightBottomWithoutRotation: {
        x: img.left + img.getScaledWidth() / 2,
        y: rightBottomY == -1 ? fallbackBottomY : rightBottomY,
      },
      centerBottomWithoutRotation: {
        x: img.left,
        y: centerBottomY == -1 ? fallbackBottomY : centerBottomY,
      },
    }
  }

  // Get the image's origin configuration
  const originX = img.originX || 'left'
  const originY = img.originY || 'top'

  // Calculate origin offset in image space
  let originOffsetX = 0
  let originOffsetY = 0

  if (originX === 'center') originOffsetX = width / 2
  else if (originX === 'right') originOffsetX = width

  if (originY === 'center') originOffsetY = height / 2
  else if (originY === 'bottom') originOffsetY = height

  // Convert image-space coordinates to canvas coordinates based on actual origin
  let leftCanvasX = img.left + (leftBottomX - originOffsetX) * img.scaleX
  let leftCanvasY = img.top + (leftBottomY - originOffsetY) * img.scaleY

  let rightCanvasX = img.left + (rightBottomX - originOffsetX) * img.scaleX
  let rightCanvasY = img.top + (rightBottomY - originOffsetY) * img.scaleY

  const centerCanvasX = img.left + (centerBottomX - originOffsetX) * img.scaleX
  const centerCanvasY = img.top + (centerBottomY - originOffsetY) * img.scaleY

  if (shape == 'concave' && !isFlatCurve(img) && img.angle != 0) {
    const leftRegionStart = left
    const leftRegionEnd = left != null ? Math.floor(left + shadowWidth * 0.08) : 0
    const rightRegionStart = right != null ? Math.floor(right) : 0
    const rightRegionEnd = right != null ? Math.floor(right - shadowWidth * 0.08) : 0

    leftBottomY = -1
    leftBottomX = -1
    rightBottomY = -1
    rightBottomX = -1

    // Find bottom-most pixel in left 5% region
    if (leftRegionStart != null && leftRegionEnd != null) {
      for (let y = height - 1; y >= 0 && leftBottomY === -1; y--) {
        for (let x = leftRegionStart; x < leftRegionEnd; x++) {
          const alpha = imageData[(y * width + x) * 4 + 3]
          if (alpha > 10) {
            if (leftBottomY === -1) {
              leftBottomY = y
              leftBottomX = x
              break
            }
          }
        }
      }
    }

    // Find bottom-most pixel in right 5% region
    for (let y = height - 1; y >= 0 && rightBottomY === -1; y--) {
      for (let x = rightRegionStart; x >= rightRegionEnd; x--) {
        const alpha = imageData[(y * width + x) * 4 + 3]
        if (alpha > 10) {
          if (rightBottomY === -1) {
            rightBottomY = y
            rightBottomX = x
            break
          }
        }
      }
    }

    leftCanvasX = img.left + (leftBottomX - originOffsetX) * img.scaleX
    leftCanvasY = img.top + (leftBottomY - originOffsetY) * img.scaleY
    rightCanvasX = img.left + (rightBottomX - originOffsetX) * img.scaleX
    rightCanvasY = img.top + (rightBottomY - originOffsetY) * img.scaleY
  }

  const centerX = (leftBottomX + rightBottomX) / 2
  let centerY = -1
  for (let y = img.height - 1; y >= 0; y--) {
    const idx = (y * img.width + centerX) * 4 + 3
    if (imageData[idx] > 10) {
      centerY = y
      break
    }
  }
  const centerBottomWithoutRotationX = img.left + (centerX - originOffsetX) * img.scaleX
  const centerBottomWithoutRotationY = img.top + (centerY - originOffsetY) * img.scaleY
  leftBottomWithoutRotationX = img.left + (leftBottomWithoutRotationX - originOffsetX) * img.scaleX
  leftBottomWithoutRotationY = img.top + (leftBottomWithoutRotationY - originOffsetY) * img.scaleY
  rightBottomWithoutRotationX =
    img.left + (rightBottomWithoutRotationX - originOffsetX) * img.scaleX
  rightBottomWithoutRotationY = img.top + (rightBottomWithoutRotationY - originOffsetY) * img.scaleY

  // Apply rotation if needed
  if (img.angle !== 0) {
    const theta = (img.angle * Math.PI) / 180
    const cos = Math.cos(theta)
    const sin = Math.sin(theta)

    const rotatePoint = (x: number, y: number) => {
      const dx = x - img.left
      const dy = y - img.top
      return {
        x: img.left + dx * cos - dy * sin,
        y: img.top + dx * sin + dy * cos,
      }
    }

    return {
      leftBottom: rotatePoint(leftCanvasX, leftCanvasY),
      rightBottom: rotatePoint(rightCanvasX, rightCanvasY),
      centerBottom: rotatePoint(centerCanvasX, centerCanvasY),
      leftBottomWithoutRotation: {
        x: leftCanvasX,
        y: leftCanvasY,
      },
      rightBottomWithoutRotation: {
        x: rightCanvasX,
        y: rightCanvasY,
      },
      centerBottomWithoutRotation: {
        x: centerCanvasX,
        y: centerCanvasY,
      },
    }
  }

  return {
    leftBottom: { x: leftCanvasX, y: leftCanvasY },
    rightBottom: { x: rightCanvasX, y: rightCanvasY },
    centerBottom: { x: centerCanvasX, y: centerCanvasY },
    leftBottomWithoutRotation: {
      x: leftCanvasX,
      y: leftCanvasY,
    },
    rightBottomWithoutRotation: {
      x: rightCanvasX,
      y: rightCanvasY,
    },
    centerBottomWithoutRotation: { x: centerCanvasX, y: centerCanvasY },
  }
}

// Helper function to get the bottom-most point of a rotated image
export function getRotatedImageBottom(img: FabricImage): { x: number; y: number } {
  const coords = img.getCoords()
  let bottomY = coords[0].y

  // Find the lowest point
  for (let i = 1; i < coords.length; i++) {
    if (coords[i].y > bottomY) {
      bottomY = coords[i].y
    }
  }

  // Adjust x according to originX
  let adjustedX = img.left!
  const halfWidth = img.getScaledWidth() / 2

  switch (img.originX) {
    case 'center':
      adjustedX = img.left!
      break
    case 'left':
      adjustedX = img.left! + halfWidth
      break
    case 'right':
      adjustedX = img.left! - halfWidth
      break
    default:
      adjustedX = img.left!
  }

  return { x: adjustedX, y: bottomY }
}

// Helper function to get the width of the rotated image (the dimension that's horizontal after rotation)
function getRotatedImageWidth(img: FabricImage): number {
  const angle = img.angle || 0
  const normalizedAngle = Math.abs(angle % 180)

  // For 90-degree rotations, the "width" becomes the original height
  if (normalizedAngle >= 85 && normalizedAngle <= 95) {
    return img.height * img.scaleY
  }

  // For other angles, calculate the effective width
  const radians = (angle * Math.PI) / 180
  const effectiveWidth =
    Math.abs(img.width * img.scaleX * Math.cos(radians)) +
    Math.abs(img.height * img.scaleY * Math.sin(radians))

  return effectiveWidth
}

function getVisualBottomLeft(img: FabricImage): { x: number; y: number } {
  const corners = img.getCoords() // [tl, tr, br, bl]
  if (!corners) return { x: img.left, y: img.top }

  let bottomLeft = corners[0]

  for (const pt of corners) {
    if (
      pt.y > bottomLeft.y || // lower on canvas
      (pt.y === bottomLeft.y && pt.x < bottomLeft.x) // tie on y, but more left
    ) {
      bottomLeft = pt
    }
  }

  return bottomLeft
}

// Get image-local X from canvas X
function getImageLocalX(img: FabricImage, canvasX: number): number {
  const originOffsetX =
    img.originX === 'center' ? img.width! / 2 : img.originX === 'right' ? img.width! : 0

  return Math.round((canvasX - img.left!) / img.scaleX + originOffsetX)
}

async function addShadowForInvertedCurve(
  leftBottom: { x: any },
  rightBottom: { x: any },
  centerBottomWithoutRotation: { y: any },
  centerBottom: { y: number },
  img: FabricImage<Partial<ImageProps>, SerializedImageProps, ObjectEvents>,
  canvas: Canvas,
): Promise<FabricImage> {
  const middlePointCanvasX = (leftBottom.x + rightBottom.x) / 2
  const middleLeftCanvasX = middlePointCanvasX - img.getScaledWidth() * 0.06
  const middleRightCanvasX = middlePointCanvasX + img.getScaledWidth() * 0.06

  const middleLeftX = getImageLocalX(img, middleLeftCanvasX)
  const middleRightX = getImageLocalX(img, middleRightCanvasX)
  let middleLeftY = -1,
    middleRightY = -1
  const el = img.getElement()
  const canvas1 = document.createElement('canvas')
  img.getCoords()
  const width = img.width
  const height = img.height
  canvas1.width = width
  canvas1.height = height
  const ctx = canvas1.getContext('2d')
  if (!ctx) return Promise.reject('No 2d context')

  ctx.drawImage(el, 0, 0)
  const imageData = ctx.getImageData(0, 0, width, height).data
  // const originOffsetY =
  //   img.originY === 'center' ? img.height / 2 : img.originY === 'bottom' ? img.height : 0
  for (let y = Math.round(img.height - 1); y >= 0; y--) {
    const indexLeft = (y * img.width + middleLeftX) * 4 + 3
    const indexRight = (y * img.width + middleRightX) * 4 + 3

    if (middleLeftY === -1 && imageData[indexLeft] > 10) {
      middleLeftY = y
    }

    if (middleRightY === -1 && imageData[indexRight] > 10) {
      middleRightY = y
    }

    if (middleLeftY !== -1 && middleRightY !== -1) break
  }
  // const middleLeftCanvasY = img.top + (middleLeftY - originOffsetY) * img.scaleY
  // const middleRightCanvasY = img.top + (middleRightY - originOffsetY) * img.scaleY
  let rotatedBottom = 0
  // if (
  //   centerBottomWithoutRotation.y - middleLeftCanvasY > 1 ||
  //   centerBottomWithoutRotation.y - middleRightCanvasY > 1
  // ) {
  //   shadow = await addImageShadow(
  //     img,
  //     shadowImages.ellipse[4], // product-middle
  //     'ellipse',
  //     canvas,
  //   )
  //   shadow.set({ layerType: 'ellipse' })
  //   rotatedBottom = centerBottom.y - shadow.getScaledHeight() / 2
  // } else {
  const shadow = await addImageShadow(
    img,
    shadowImages.square[3], // square/Dark-neutral-curved
    'square',
    canvas,
  )
  shadow.set({ layerType: 'square' })
  rotatedBottom = centerBottom.y
  // }
  shadow.top = rotatedBottom + shadow.getScaledHeight() / 2
  return shadow
}

/**
 * Step 1: Extract bottom contour points of the image
 */
function getBottomContour(fabricImage: FabricImage, bottomPercent = 0.05) {
  const imgEl = fabricImage._element
  const width = fabricImage.width
  const height = fabricImage.height

  const offCanvas = document.createElement('canvas')
  offCanvas.width = width
  offCanvas.height = height
  const ctx = offCanvas.getContext('2d')
  if (ctx == null) {
    return
  }
  ctx.drawImage(imgEl, 0, 0)

  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  const bottomStartY = Math.floor(height * (1 - bottomPercent))
  const bottomPoints = []

  for (let x = 0; x < width; x++) {
    let found = false
    for (let y = height - 1; y >= bottomStartY; y--) {
      const idx = (y * width + x) * 4
      const alpha = data[idx + 3]
      if (alpha > 10) {
        bottomPoints.push({ x, y })
        found = true
        break
      }
    }
    // If no pixel found in this column, mark as empty
    if (!found) {
      bottomPoints.push({ x, y: height }) // transparent column
    }
  }

  return bottomPoints
}

/**
 * Step 2: Analyze points and classify bottom shape
 */
function classifyBottomShape(points: any[], imageHeight: number, imageWidth: number) {
  const threshold = 5 // tolerance for flatness
  points = points.filter((pt) => pt.y != imageHeight) // filter out transparent columns
  let yValues = points.map((pt) => imageHeight - pt.y) // convert to Cartesian

  if (Array.isArray(yValues) && yValues.length > 30) {
    yValues = yValues.slice(5, -5) // remove first and last 5 points for better analysis
  }

  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const range = maxY - minY

  // 🆕 Tilted check (left-to-right increasing till 85%)
  const cutoffIndex = Math.floor(yValues.length * 0.85);
  let increasingCount = 0;
  for (let i = 1; i < cutoffIndex; i++) {
    if (yValues[i-1] > yValues[i]) increasingCount++;
  }

  // If at least 80% of first 85% points are increasing, call it 'tilted'
  const increasingRatio = increasingCount / (cutoffIndex - 1);
  if (increasingRatio > 0.5) return "tilted";

  // Flat check
  if (range <= threshold * 2) return 'flat'

  // Wavy check
  if (isWaveShape(yValues)) return 'wavy';

  const widthToHeightRatio = imageWidth / imageHeight

  if(widthToHeightRatio < 1.5 && isPouchShape(yValues)) {
    return 'pouch'
  }

  // logic for other shapes
  const midY = Math.floor(yValues.length / 2)
  const left = yValues[0]
  const right = yValues[yValues.length - 1]
  const mid = yValues[midY]

  if (mid < left && mid < right && range > threshold * 2) return 'concave'
  if (mid > left && mid > right && range > threshold * 2) return 'convex'

  return 'irregular'
}

/**
 * Helper function to calculate variation in a section
 */
function calculateVariation(section: number[]): number {
  if (section.length < 2) return 0

  let totalVariation = 0
  for (let i = 1; i < section.length; i++) {
    totalVariation += Math.abs(section[i] - section[i - 1])
  }
  return totalVariation / section.length
}

/**
 * Helper function to calculate slope of a section
 */
function calculateSlope(section: number[]): number {
  if (section.length < 2) return 0

  const firstHalf = section.slice(0, Math.floor(section.length / 2))
  const secondHalf = section.slice(Math.floor(section.length / 2))

  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length

  return (secondAvg - firstAvg) / (section.length / 2)
}

/**
 * Enhanced function to get bottom contour with more detailed analysis
 */
function getBottomContourEnhanced(fabricImage: FabricImage, bottomPercent = 0.05) {
  const imgEl = fabricImage._element
  const width = fabricImage.width
  const height = fabricImage.height

  const offCanvas = document.createElement('canvas')
  offCanvas.width = width
  offCanvas.height = height
  const ctx = offCanvas.getContext('2d')
  if (ctx == null) {
    return
  }
  ctx.drawImage(imgEl, 0, 0)

  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  const bottomStartY = Math.floor(height * (1 - bottomPercent))
  const bottomPoints = []

  // Get more detailed bottom contour
  for (let x = 0; x < width; x++) {
    let found = false
    let bottomY = height

    // Look for the actual bottom-most pixel in this column
    for (let y = height - 1; y >= bottomStartY; y--) {
      const idx = (y * width + x) * 4
      const alpha = data[idx + 3]
      if (alpha > 10) {
        bottomY = y
        found = true
        break
      }
    }

    bottomPoints.push({ x, y: bottomY, hasPixel: found })
  }

  // Smooth the contour to remove noise
  const smoothedPoints = smoothContour(bottomPoints)

  return smoothedPoints
}

/**
 * Helper function to smooth the contour
 */
function smoothContour(points: any[], windowSize = 5): any[] {
  const smoothed = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < points.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(points.length - 1, i + halfWindow)

    let sumY = 0
    let count = 0

    for (let j = start; j <= end; j++) {
      if (points[j].hasPixel) {
        sumY += points[j].y
        count++
      }
    }

    const smoothedY = count > 0 ? sumY / count : points[i].y
    smoothed.push({
      x: points[i].x,
      y: smoothedY,
      hasPixel: points[i].hasPixel
    })
  }

  return smoothed
}

/**
 * Main function to get bottom shape label with enhanced detection
 */
export function getBottomShapeLabel(fabricImage: FabricImage) {
  const bottomPoints = getBottomContourEnhanced(fabricImage, 0.15) // Use larger sample for box detection
  if (!bottomPoints) return 'irregular'

  const shapeLabel = classifyBottomShape(
    bottomPoints,
    fabricImage.height,
    fabricImage.width
  )

  return shapeLabel
}
export async function syncShadow(img: any, shadow: FabricImage, canvas: Canvas) {
  const editorStore = useEditorStore()

  // Remove existing shadows
  const presentShadows = canvas
    .getObjects()
    .filter(
      (obj: any) =>
        (obj.layerName === 'Shadow' || obj.layerName === layerNames.AIShadow) &&
        obj.layerId === (shadow as FabricImage & { layerId?: string }).layerId,
    )
  presentShadows.forEach((s) => {
    canvas.remove(s)
  })
  canvas.requestRenderAll()

  let shadowWidth
  function getSideMidpoints(img: FabricImage): {
    leftMid: { x: number; y: number }
    rightMid: { x: number; y: number }
    bottomLeft: { x: number; y: number }
    bottomRight: { x: number; y: number }
  } {
    const { tl, tr, bl, br } = img.oCoords!

    const leftMid = {
      x: (tl.x + bl.x) / 2,
      y: (tl.y + bl.y) / 2,
    }

    const rightMid = {
      x: (tr.x + br.x) / 2,
      y: (tr.y + br.y) / 2,
    }

    return { bottomLeft: bl, bottomRight: br, leftMid, rightMid }
  }

  // Reusable function to determine quadrant (Q1: 1-90, Q2: 90-180, Q3: 180-270, Q4: 270-359, Q5: 0-1 or 359-360)
  const getQuadrant = (angle: number): number => {
    const normalizedAngle = (angle + 360) % 360
    if (
      (normalizedAngle >= 0 && normalizedAngle < 1) ||
      (normalizedAngle >= 359 && normalizedAngle < 360) ||
      (normalizedAngle >= 89 && normalizedAngle < 91) ||
      (normalizedAngle >= 269 && normalizedAngle < 271) ||
      (normalizedAngle >= 179 && normalizedAngle < 181)
    )
      return 5 // Q5
    if (normalizedAngle >= 1 && normalizedAngle < 90) return 1 // Q1
    if (normalizedAngle >= 90 && normalizedAngle < 180) return 2 // Q2
    if (normalizedAngle >= 180 && normalizedAngle < 270) return 3 // Q3
    return 4 // Q4
  }

  const quadrant = getQuadrant(img.angle)
  const normalizedAngle = Math.abs((img.angle + 360) % 360)

  // Check if image is rotated approximately 90 degrees (85-95 degrees range for tolerance)
  const isRotated90 =
    (normalizedAngle >= 85 && normalizedAngle <= 95) ||
    (normalizedAngle >= 265 && normalizedAngle <= 275)

  const rotatedBottom = getRotatedImageBottom(img)

  if (isRotated90) {
    const reversedAspectRatio = (img.width * img.scaleX) / (img.height * img.scaleY)

    // Get the bottom point of the rotated image

    if (reversedAspectRatio <= 1.5) {
      // For 90-degree rotated images, use square shadow positioned below the rotated image
      shadow = await addImageShadow(
        img,
        shadowImages.square[3], // Dark-neutral-curved square shadow
        'square',
        canvas,
      )
      shadow.set({ layerType: 'square' })
      shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2
    } else {
      shadow = await addImageShadow(
        img,
        shadowImages.ellipse[4], // product-middle
        'ellipse',
        canvas,
      )
      shadow.set({ layerType: 'ellipse' })
      shadow.top = rotatedBottom.y
    }
    shadow.set('globalCompositeOperation', 'multiply')

    // Get the width of the rotated image (which becomes the shadow width)
    const rotatedWidth = getRotatedImageWidth(img)

    // Scale shadow to match the width of the rotated image
    shadow.scaleToWidth(rotatedWidth)

    // Position shadow at the center bottom of the rotated image
    shadow.left = rotatedBottom.x
    shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2
    shadow.angle = 0 // Keep shadow horizontal regardless of image rotation
    shadow.setCoords()
  } else {
    // Existing logic for other rotations and non-rotated images

    // Determine shadow position (left or right) and corresponding bottom point
    const { bottomLeft, bottomRight, leftMid, rightMid } = getSideMidpoints(img)

    let isLeftShadow = false
    // Determine if shadow should be on left or right side based on rotation and geometry
    if (quadrant === 1) {
      // For quadrants 1 and 3, check which side is lower
      if (bottomLeft.y > rightMid.y) {
        // Left side is lower, shadow goes to right
        isLeftShadow = false
      } else {
        // Right side is lower, shadow goes to left
        isLeftShadow = true
      }
    } else if (quadrant === 2) {
      isLeftShadow = true
    } else if (quadrant == 3) {
      isLeftShadow = true
    } else if (quadrant === 4) {
      if (bottomRight.y < leftMid.y) {
        isLeftShadow = false
      } else {
        isLeftShadow = true
      }
    } else {
      // For other cases, check which side is lower
      if (bottomRight.y < leftMid.y) {
        // Right side is lower, shadow goes to right
        isLeftShadow = false
      } else {
        // Left side is lower, shadow goes to left
        isLeftShadow = true
      }
    }
    const shape = getBottomShapeLabel(img)
    
    const finalShadowUrl = isLeftShadow ? rotateShadowImages.left_thin : rotateShadowImages.right_thin;

    const {
        leftBottom,
        rightBottom,
        centerBottom,
        centerBottomWithoutRotation,
        leftBottomWithoutRotation,
        rightBottomWithoutRotation,
      } = getBottomCanvasPoints(img, shape)
    if (quadrant === 1 || quadrant === 2 || quadrant === 3 || quadrant === 4) {
      if (shadow) {
        shadow = await addImageShadow(img, finalShadowUrl, 'rotate', canvas)
        if (
        shape == 'concave' &&
        centerBottomWithoutRotation.y - Math.min(leftBottomWithoutRotation.y, rightBottomWithoutRotation.y) >= 8 &&
        Math.abs(img.angle % 360) > 0 &&
        Math.abs(img.angle % 360) <= 10
        ) {
          let bottomCurve = 0
          const threshold = 12
          if (img.angle < 0) {
            bottomCurve = centerBottomWithoutRotation.y - leftBottomWithoutRotation.y + threshold
          } else if (img.angle > 0) {
            bottomCurve = centerBottomWithoutRotation.y - rightBottomWithoutRotation.y + threshold
          }

          if (bottomCurve > (shadow.getScaledHeight() / 2)) {
            shadow.scaleY *= (bottomCurve * 2) / shadow.getScaledHeight()
          }
        }
      }
      // Get bottom points based on visible pixels
      let shadowBottomPoint = rightBottom.x // default to right bottom point

      const imageBottomPoint = centerBottom

      if (quadrant === 1) {
        if (
          shape == 'concave' &&
          centerBottomWithoutRotation.y -
            Math.min(leftBottomWithoutRotation.y, rightBottomWithoutRotation.y) >
            10 &&
          Math.abs(img.angle % 360) <= 4
        ) {
          imageBottomPoint.y = rightBottom.y
        } else {
          imageBottomPoint.y = rightBottom.y
        }
        // For quadrants 1 and 3, check which side is lower
        if (bottomLeft.y > rightMid.y) {
          shadowBottomPoint =
            rightBottom.x -
            shadow.getScaledWidth() / 2
        } else {
          shadowBottomPoint = rightBottom.x + img.getScaledHeight() / 2
          shadowWidth = img.getScaledHeight()
        }
      } else if (quadrant === 2) {
        const bottomPoint = getVisualBottomLeft(img)
        shadowBottomPoint = bottomPoint.x + shadow.getScaledWidth() / 2
        imageBottomPoint.y = bottomPoint.y
      } else if (quadrant == 3) {
        const bottomPoint = getVisualBottomLeft(img)
        shadowBottomPoint = bottomPoint.x + shadow.getScaledWidth() / 2
        imageBottomPoint.y = bottomPoint.y
      } else if (quadrant === 4) {
        if (
          shape == 'concave' &&
          centerBottomWithoutRotation.y -
            Math.min(leftBottomWithoutRotation.y, rightBottomWithoutRotation.y) >
            10 &&
          Math.abs(img.angle % 360) <= 4
        ) {
          imageBottomPoint.y = leftBottom.y
        } else {
          imageBottomPoint.y = leftBottom.y
        }
        if (bottomRight.y < leftMid.y) {
          shadowWidth = img.getScaledHeight()
          shadowBottomPoint = leftBottom.x - img.getScaledHeight() / 2
        } else {
          shadowBottomPoint =
            leftBottom.x +
            shadow.getScaledWidth() / 2
        }
      } else {
        // For other cases, check which side is lower
        if (bottomRight.y < leftMid.y) {
          // Right side is lower, shadow goes to right
          shadowBottomPoint = rightBottom.x
        } else {
          // Left side is lower, shadow goes to left
          shadowBottomPoint = leftBottom.x
        }
      }
      shadow.set('globalCompositeOperation', 'multiply')

      shadow.left = shadowBottomPoint
      shadow.top = imageBottomPoint.y
      shadow.angle = 0
      shadow.set({ layerType: 'rotate' })
      if (shadowWidth) {
        shadow.scaleToWidth(shadowWidth)
      }
      shadow.setCoords()
    } else {
      // Handle bottom shadows for non-rotated images
      let offset = 0
      const shape = getBottomShapeLabel(img)
      const {
        centerBottom,
        leftBottomWithoutRotation,
        rightBottomWithoutRotation,
        centerBottomWithoutRotation,
      } = getBottomCanvasPoints(img, shape)
      const bounds = getLeftRightInBottomVisibleRegion(img, 0.05)
      const { left, right } = bounds
      if (left != null && right != null) {
        shadowWidth = (right - left) * img.scaleX
      }
      const irregularShaped = isIrregularShaped(img)

      if (shape == 'concave' && irregularShaped) {
        shadow = await addImageShadow(
          img,
          shadowImages.ellipse[4], // product-middle
          'ellipse',
          canvas,
        )
        offset = shadow.getScaledHeight() / 2
        shadow.set({ layerType: 'ellipse' })
        shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2 - offset
      } else if (shadowWidth ? shadowWidth <= img.getScaledWidth() * 0.5 : false) {
        shadow = await addImageShadow(
          img,
          shadowImages.ellipse[4], // product-middle
          'ellipse',
          canvas,
        )
        offset = shadow.getScaledHeight() / 2
        shadow.set({ layerType: 'ellipse' })
        // NEW: Move shadow slightly above for grounded look
        shadow.top =
          rotatedBottom.y + shadow.getScaledHeight() / 2 - offset - shadow.getScaledHeight() * 0.2
      }
      else if(shape == 'tilted'){
        shadow = await addImageShadow(
          img,
          shadowImages.square[19], // square/Dark-neutral-curved
          'square',
          canvas,
          'tilted'
        )
        shadow.top = rightBottom.y + shadow.getScaledHeight() * 0.2
      }
       else if(shape == 'pouch'){
        shadow = await addImageShadow(
          img,
          shadowImages.square[17], // product-middle
          'square',
          canvas,
        )
        offset = 0
        shadow.set({ layerType: 'square' })
        // NEW: Move shadow slightly above for grounded look
        shadow.top =
          leftBottom.y + shadow.getScaledHeight()/2  // Adjust 0.1 factor (e.g., 0.05 for less movement) 
      } else if (shape == 'wavy') {
        shadow = await addImageShadow(
          img,
          shadowImages.square[17],
          'square',
          canvas,
        )
        offset = 0
        shadow.set({ layerType: 'square' })
        shadow.top = leftBottom.y + shadow.getScaledHeight() / 2 - getBottomWaveCurvePoint(img)
        shadow.scaleX -= 0.02
      } else if (
        Math.abs(leftBottomWithoutRotation.y - centerBottomWithoutRotation.y) <=
        img.getScaledWidth() * 0.02 ||
        Math.abs(rightBottomWithoutRotation.y - centerBottomWithoutRotation.y) <=
        img.getScaledWidth() * 0.02
      ) {
        shadow = await addImageShadow(
          img,
          shadowImages.square[3], // square/Dark-neutral-curved
          'square',
          canvas,
        )
        offset = 0
        shadow.set({ layerType: 'square' })
        rotatedBottom.y = centerBottom.y
        shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2 - offset
      } else if (shape == 'concave') {
        shadow = await addImageShadow(
          img,
          shadowImages.ellipse[4], // product-middle
          'ellipse',
          canvas,
        )
        offset = shadow.getScaledHeight() / 2
        shadow.set({ layerType: 'ellipse' })
        shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2 - offset
      } else if (shape == 'convex') {
        shadow = await addShadowForInvertedCurve(
          leftBottomWithoutRotation,
          rightBottomWithoutRotation,
          centerBottomWithoutRotation,
          centerBottom,
          img,
          canvas,
        )
      } else {
        shadow = await addImageShadow(
          img,
          shadowImages.square[3], // square/Dark-neutral-curved
          'square',
          canvas,
        )
        offset = 0
        shadow.set({ layerType: 'square' })
        rotatedBottom.y = centerBottom.y
        shadow.top = rotatedBottom.y + shadow.getScaledHeight() / 2 - offset
      }

      shadow.setCoords()
    }

    shadow.set({ isLeftShadow })
  }

  editorStore.setIsDropShadow(true)
  editorStore.setIsAiShadow(false)

  canvas.add(shadow)
  let productIndex = canvas.getObjects().indexOf(img)
  if (productIndex == -1) {
    const product = canvas.getObjects().find((obj: any) => obj.layerName === img.layerName);
    if (product) {
      productIndex = canvas.getObjects().indexOf(product)
    }
  }
  canvas.moveObjectTo(shadow, productIndex)

  canvas.requestRenderAll()
}

// unique key to track handlers per image
const SHADOW_SYNC_KEY = 'ShadowSyncHandlers'

type ShadowSyncHandlers = {
  moving: () => void
  scaling: () => void
  modified: () => void
  updateShadow: (shadow: FabricImage) => void
}

export function syncContactShadow(
  img: FabricImage,
  shadow: FabricImage,
  canvas: Canvas
): void {
  const existing = (img as any)[SHADOW_SYNC_KEY] as ShadowSyncHandlers | undefined

  // if handlers already exist → just update the shadow reference
  if (existing) {
    existing.updateShadow(shadow)
    return
  }

  let timeoutId: number | undefined
  let currentShadow = shadow
  let currentShadowType =
    (currentShadow as any).layerName == layerNames.AIShadow
      ? shadowTypes.AI_SHADOW
      : shadowTypes.CONTACT_SHADOW

  const debouncedSync = () => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      if (
        currentShadowType == shadowTypes.AI_SHADOW &&
        (currentShadow as any).layerName == layerNames.AIShadow &&
        isFlatBase(img.angle)
      ) {
        const aiShadowProperties = (currentShadow as any).aiShadowProperties as AIShadowProperties
        syncAIShadow(img, currentShadow, canvas, aiShadowProperties)
        currentShadowType = shadowTypes.AI_SHADOW
      } else if (currentShadowType != shadowTypes.AI_SHADOW && isFlatBase(img.angle)) {
        try {
          generateAIShadows(canvas, [img])
          currentShadowType = shadowTypes.AI_SHADOW
        } catch {
          syncShadow(img, currentShadow, canvas)
          currentShadowType = shadowTypes.CONTACT_SHADOW
        }
      } else {
        syncShadow(img, currentShadow, canvas)
        currentShadowType = shadowTypes.CONTACT_SHADOW
      }
      timeoutId = undefined
      currentShadow.visible = true
      canvas.requestRenderAll()
    }, 50)
  }

  const hideShadow = () => {
    currentShadow.visible = false
    canvas.requestRenderAll()
  }

  const handlers: ShadowSyncHandlers = {
    moving: hideShadow,
    scaling: hideShadow,
    modified: debouncedSync,
    updateShadow: (newShadow: FabricImage) => {
      currentShadow = newShadow
      currentShadowType =
        (currentShadow as any).layerName == layerNames.AIShadow
          ? shadowTypes.AI_SHADOW
          : shadowTypes.CONTACT_SHADOW
    },
  }

  ;(img as any)[SHADOW_SYNC_KEY] = handlers

  img.on('moving', handlers.moving)
  img.on('scaling', handlers.scaling)
  img.on('modified', handlers.modified)
}

export function detachContactShadow(img: FabricImage): void {
  const handlers = (img as any)[SHADOW_SYNC_KEY] as ShadowSyncHandlers | undefined
  if (!handlers) return

  img.off('moving', handlers.moving)
  img.off('scaling', handlers.scaling)
  img.off('modified', handlers.modified)

  delete (img as any)[SHADOW_SYNC_KEY]
}

export function syncAIShadow(
  img: any,
  currentShadow: any,
  canvas: any,
  aishadowProperties: AIShadowProperties,
) {
  // === Generation reference values (from saved properties) ===
  const pgLeft = aishadowProperties.productGeneratedLeft
  const pgTop = aishadowProperties.productGeneratedTop
  const pgScaleX = aishadowProperties.productGeneratedScaleX
  const pgScaleY = aishadowProperties.productGeneratedScaleY
  const pgOriginX = (aishadowProperties.productGeneratedOriginX || 'left').toString()
  const pgOriginY = (aishadowProperties.productGeneratedOriginY || 'top').toString()

  // === Current product transform (live) ===
  const curLeft = img.left
  const curTop = img.top
  const curScaleX = img.scaleX
  const curScaleY = img.scaleY
  const curOriginX = (img.originX || 'left').toString()
  const curOriginY = (img.originY || 'top').toString()

  // === Product base size (unscaled) ===
  // Prefer storing productGeneratedWidth/Height in the meta at generation time.
  // If not available, we use img.width/img.height (assuming base image hasn't changed).
  const productBaseWidth = (aishadowProperties.productGeneratedWidth ?? img.width) as number
  const productBaseHeight = (aishadowProperties.productGeneratedHeight ?? img.height) as number

  // === Helper: compute origin offset (in canvas pixels) for a given origin type ===
  function originOffsetX(origin: string, baseWidth: number, scaleX: number) {
    // returns how many canvas pixels to add/subtract from 'left' to get top-left
    // origin: 'left' | 'center' | 'right'
    if (origin === 'left') return 0
    if (origin === 'center') return (baseWidth * scaleX) / 2
    if (origin === 'right') return baseWidth * scaleX
    // fallback assume left
    return 0
  }
  function originOffsetY(origin: string, baseHeight: number, scaleY: number) {
    // origin: 'top' | 'center' | 'bottom'
    if (origin === 'top') return 0
    if (origin === 'center') return (baseHeight * scaleY) / 2
    if (origin === 'bottom') return baseHeight * scaleY
    return 0
  }

  // === 1) Normalize generation pos -> generation top-left in canvas coords ===
  const pgOriginOffsetX = originOffsetX(pgOriginX, productBaseWidth, pgScaleX)
  const pgOriginOffsetY = originOffsetY(pgOriginY, productBaseHeight, pgScaleY)

  const productGeneratedTopLeftX = pgLeft - pgOriginOffsetX
  const productGeneratedTopLeftY = pgTop - pgOriginOffsetY

  // === 2) Normalize current pos -> current top-left in canvas coords ===
  const curOriginOffsetX = originOffsetX(curOriginX, productBaseWidth, curScaleX)
  const curOriginOffsetY = originOffsetY(curOriginY, productBaseHeight, curScaleY)

  const productCurrentTopLeftX = curLeft - curOriginOffsetX
  const productCurrentTopLeftY = curTop - curOriginOffsetY

  // === Background (shadow) generation reference ===
  // You earlier used bgGeneratedLeft=0, bgGeneratedTop=0 because the shadow image
  // was full-canvas. Keep that unless you have a different placement at gen.
  const bgGeneratedLeft = 0
  const bgGeneratedTop = 0

  // === Decide the anchor point in bg pixels ===
  // If you recorded a specific anchor pixel (ax, ay), use it.
  // Otherwise this example uses the center-bottom of the product area as a default anchor,
  // relative to the productGenerated position (you can adjust).
  // BUT ideally you should detect the shadow's anchor or bounding box.
  // For backwards compatibility with your prior code you can compute:
  const ax = aishadowProperties.shadowAnchorAx ?? (productGeneratedTopLeftX + (productBaseWidth * pgScaleX) / 2) // fallback
  const ay = aishadowProperties.shadowAnchorAy ?? (productGeneratedTopLeftY + (productBaseHeight * pgScaleY) / 2) // fallback
  // If `shadowAnchorAx/Ay` are stored as full-canvas coords (like 684/642), they can be used directly.
  // If they were stored as 'in-bg' pixel coords, use: const shadowAnchorGlobalX_atGen = bgGeneratedLeft + ax;

  // If ax/ay are stored as in-bg pixels (like earlier examples), interpret them as that:
  // Here we assume ax/ay are the bg-pixel coordinates at generation (as in your earlier flows).
  const shadowAnchorGlobalX_atGen = bgGeneratedLeft + ax
  const shadowAnchorGlobalY_atGen = bgGeneratedTop + ay

  // === 3) Convert anchor->product-local (unscaled) using generation top-left ===
  // This gives us the anchor's location in product-local (unscaled) coordinates
  const anchorOffsetProductLocalX =
    (shadowAnchorGlobalX_atGen - productGeneratedTopLeftX) / pgScaleX
  const anchorOffsetProductLocalY =
    (shadowAnchorGlobalY_atGen - productGeneratedTopLeftY) / pgScaleY

  // === 4) Desired anchor global with current product transform ===
  const desiredAnchorGlobalX = productCurrentTopLeftX + anchorOffsetProductLocalX * curScaleX
  const desiredAnchorGlobalY = productCurrentTopLeftY + anchorOffsetProductLocalY * curScaleY

  // === 5) Scale the shadow proportional to product scale change (as before) ===
  const scaleRatioX = curScaleX / pgScaleX
  const scaleRatioY = curScaleY / pgScaleY

  const shadowOriginalScaleX = 1
  const shadowOriginalScaleY = 1

  currentShadow.scaleX = shadowOriginalScaleX * scaleRatioX
  currentShadow.scaleY = shadowOriginalScaleY * scaleRatioY

  // === 6) Place shadow so its anchor pixel (ax,ay) sits at desired anchor ===
  currentShadow.left = desiredAnchorGlobalX - ax * currentShadow.scaleX
  currentShadow.top = desiredAnchorGlobalY - ay * currentShadow.scaleY

  // Fine rounding
  currentShadow.left = parseFloat(currentShadow.left.toFixed(3))
  currentShadow.top = parseFloat(currentShadow.top.toFixed(3))

  canvas.renderAll()
}

export async function generateAIShadows(
  canvas: Canvas,
  objects: any[] | null = null,
) {
  const creativeService = new CreativeService()
  const appStore = useAppStore()
  const brandStore = useBrandStore()
  const productStore = useProductStore()
  const editorStore = useEditorStore()
  const placementStore = usePlacementStore()

  const productImages: ShadowProducts[] = []
  const inputProducts: any[] = []
  let aiShadows: ShadowProducts[] = []

  // Remove existing ai shadows
  let existingAiShadows: any[] = []

  if (objects?.length) {
    existingAiShadows = canvas
      .getObjects()
      .filter((obj: any) => obj.layerName === layerNames.AIShadow && objects.some((o) => o.layerId === obj.layerId))
  } else {
    existingAiShadows = canvas
      .getObjects()
      .filter((obj: any) => obj.layerName === layerNames.AIShadow)
  }

  existingAiShadows.forEach((s) => {
    canvas.remove(s)
  })

  const products: any[] = objects ?? canvas
    .getObjects()
    .filter((o: any) => o.layerName.includes(layerNames.Product))

  for (let i = 0; i < products.length; i++) {
    const selectedProduct = productStore.selectedProducts.find(
      (p) => p.id === products[i].productId,
    ) as ProductWithInfo

    const activeProduct = editorStore.activeProducts.find(
      (p) => p.id === products[i].productId,
    ) as ProductWithInfo

    const productInfo = selectedProduct || activeProduct

    if (productInfo && productInfo.productInfo?.shadowInfo?.shadowUrl) {
      // AI shadow already exists for this product, skip generation
      continue
    }

    const tempCavnas = new StaticCanvas(undefined, {
      backgroundColor: 'transparent',
      width: placementStore.selectedPlacement?.creativeSetGuidelines.ad_size.width,
      height: placementStore.selectedPlacement?.creativeSetGuidelines.ad_size.height,
    })

    const cloned = await products[i].clone()

    // Center the product in the canvas
    const verticalSpacePercentage = 20
    const horizontalSpacePercentage = 30
    
    const availableHeight = tempCavnas.height - (tempCavnas.height / 100 * (verticalSpacePercentage * 2))
    const availableWidth = tempCavnas.width - (tempCavnas.width / 100 * (horizontalSpacePercentage * 2))

    const img = await FabricImage.fromURL(cloned.getSrc())

    const scaleX = availableWidth / img.width
    const scaleY = availableHeight / img.height
    
    const scale = Math.min(scaleX, scaleY)

    img.set({
      productId: products[i].productId,
      scaleX: scale,
      scaleY: scale,
      left: tempCavnas.width / 2,
      top: tempCavnas.height / 2,
      originX: "center",
      originY: "center",
      layerName: layerNames.Product
    })
    
    tempCavnas.add(img)
    tempCavnas.renderAll()

    inputProducts.push(img)

    const imageData = tempCavnas.toDataURL({
      format: 'png',
      multiplier: 1,
      enableRetinaScaling: false,
    })

    const coordinates = getCoordinates(
      img.left,
      img.top,
      img.width * img.scaleX,
      img.height * img.scaleY,
      "center",
      "center"
    )

    productImages.push({
      productId: products[i].productId,
      productName: productInfo.parentProductName,
      coordinates: coordinates,
      inputImage: imageData,
      width: tempCavnas.width,
      height: tempCavnas.height,
    })
  }

  if (productImages.length !== 0 && appStore.selectedBusinessProfileId && brandStore.selectedBrand) {
    const requestData: GenerateAIShadowsRequest = {
      brandName: brandStore.selectedBrand.name,
      products: productImages,
    }
  
    aiShadows = await creativeService.generateAIShadows(
      appStore.selectedBusinessProfileId,
      requestData,
    )
  }

  aiShadows.forEach(async (aiShadow: ShadowProducts) => {
    const product = inputProducts.find((o: any) => o.productId === aiShadow.productId)

    if (product && aiShadow.outputImageUrl && placementStore.selectedPlacement) {
      const aishadowProperties: AIShadowProperties = {
        shadowUrl: aiShadow.outputImageUrl,
        productGeneratedLeft: product.left,
        productGeneratedTop: product.top,
        productGeneratedScaleX: product.scaleX,
        productGeneratedScaleY: product.scaleY,
        productGeneratedOriginX: product.originX.toString(),
        productGeneratedOriginY: product.originY.toString(),
        productGeneratedWidth: product.width,
        productGeneratedHeight: product.height,
        shadowAnchorAx: product.left - (product.width * product.scaleX) / 2,
        shadowAnchorAy: product.top + (product.height * product.scaleY) / 2,
        canvasGeneratedWidth: placementStore.selectedPlacement.creativeSetGuidelines.ad_size.width,
        canvasGeneratedHeight: placementStore.selectedPlacement.creativeSetGuidelines.ad_size.height,
      }

      productStore.storeShadowProperties(aiShadow.productId, aishadowProperties)
      editorStore.storeShadowProperties(aiShadow.productId, aishadowProperties)
    }
  })

  for (let i = 0; i < products.length; i++) {
    const selectedProduct = productStore.selectedProducts.find(
      (p) => p.id === products[i].productId,
    ) as ProductWithInfo

    const activeProduct = editorStore.activeProducts.find(
      (p) => p.id === products[i].productId,
    ) as ProductWithInfo

    const productInfo = selectedProduct || activeProduct

    if (productInfo && productInfo.productInfo?.shadowInfo?.shadowUrl) {
      await addAIShadow(products[i], canvas, productInfo.productInfo.shadowInfo)
    }
  }

  // Remove existing contact shadows for the product
  const existingContactShadows = canvas
    .getObjects()
    .filter((obj: any) => obj.layerName === 'Shadow' && products.some((p) => p.layerId === obj.layerId))

  existingContactShadows.forEach((s: any) => {
    canvas.remove(s)
  })
  editorStore.setIsAiShadow(true)
}

export async function addAIShadow(product: any, canvas: any, aiShadowProperties: AIShadowProperties) {
  const aiShadow: any = await FabricImage.fromURL(aiShadowProperties.shadowUrl, {
    crossOrigin: 'anonymous',
  })

  aiShadow.set({
    left: 0,
    top: 0,
    layerName: layerNames.AIShadow,
    scaleX: aiShadowProperties.canvasGeneratedWidth / aiShadow.width,
    scaleY: aiShadowProperties.canvasGeneratedHeight / aiShadow.height,
    globalCompositeOperation: 'multiply',
    layerId: product.layerId,
    selectable: false,
    evented: false,
    aiShadowProperties: aiShadowProperties,
    opacity: 0.7,
  })

  if (!product.layerId) {
    product.set('layerId', uuidv4())
  }

  aiShadow.set('layerId', product.layerId)
  product.set({
    aiShadowProperties
  })

  canvas.add(aiShadow)

  // Move shadow above the duo tone
  const duoToneShapes = canvas
    .getObjects()
    .filter(
      (obj: any) =>
        obj.layerName == layerNames.BackgroundShape ||
        obj.layerName == layerNames.BackgroundTexture,
    )

  // Send the top product to behind the bottom products
  canvas.moveObjectTo(aiShadow, duoToneShapes.length)
  syncContactShadow(product, aiShadow, canvas)
  syncAIShadow(product, aiShadow, canvas, aiShadowProperties)
  canvas.requestRenderAll()
}

const getCoordinates = (left: number, top: number, width: number, height: number, originX = "left", originY = "top") => {
  let x1, y1, x2, y2;

  // Horizontal origin
  switch (originX) {
    case "left":
      x1 = left;
      x2 = left + width;
      break;

    case "center":
      x1 = left - width / 2;
      x2 = left + width / 2;
      break;

    case "right":
      x1 = left - width;
      x2 = left;
      break;

    default:
      throw new Error(`Invalid originX: ${originX}`);
  }

  // Vertical origin
  switch (originY) {
    case "top":
      y1 = top;
      y2 = top + height;
      break;

    case "center":
      y1 = top - height / 2;
      y2 = top + height / 2;
      break;

    case "bottom":
      y1 = top - height;
      y2 = top;
      break;

    default:
      throw new Error(`Invalid originY: ${originY}`);
  }

  return [x1, y1, x2, y2];
}

export const isFlatBase = (angle: number) => {
  const normalizedAngle = (angle + 360) % 360;
  if (
    (normalizedAngle >= 0 && normalizedAngle < 1) ||
    (normalizedAngle >= 359 && normalizedAngle < 360) ||
    (normalizedAngle >= 89 && normalizedAngle < 91) ||
    (normalizedAngle >= 269 && normalizedAngle < 271) ||
    (normalizedAngle >= 179 && normalizedAngle < 181)
  ) {
    return true
  }

  return false
}

export function createShadow(
  target: FabricImage,
  placement: string,
  lightDirection: string = DIRECTIONS.TOP,
): Shadow | null {
  if (lightDirection === DIRECTIONS.NONE) {
    return null
  }

  switch (placement) {
    case placementTypes.Top:
      return createDropShadow(target, lightDirection)
    case PLACEMENT_TYPES.FLOAT:
      return lightDirection === DIRECTIONS.BEHIND
        ? createGlowShadow(target)
        : createAmbientShadow(target)
    case PLACEMENT_TYPES.CORNER:
      return lightDirection === DIRECTIONS.BEHIND
        ? createGlowShadow(target)
        : createDropShadow(target, lightDirection)

    case placementTypes.Stack:
    case placementTypes.Collage:
      return createDropShadow(target, lightDirection)

    case placementTypes.Vertical:
      switch (lightDirection) {
        case DIRECTIONS.FRONT:
          return createAmbientShadow(target)
        case DIRECTIONS.BEHIND:
          return createGlowShadow(target)
      }
      break

    default:
      return createDropShadow(target, lightDirection)
  }
  return createDropShadow(target, lightDirection)
}

export async function addImageShadow(
  fabricImage: FabricImage,
  shadowImageUrl: string,
  shadowType: string,
  canvas: Canvas,
  shadowShape: string = 'square',
): Promise<FabricImage> {
  const shadowImg = await FabricImage.fromURL(shadowImageUrl, { crossOrigin: 'anonymous' })
  const bounds = getLeftRightInBottomVisibleRegion(fabricImage, 0.05)
  const { left, right } = bounds
  if (left == null || right == null) return shadowImg

  // Calculate scaled width based on visible region
  const scaledWidth = shadowShape == 'tilted' ? fabricImage.getScaledWidth() * 1.05 : (right - left) * fabricImage.scaleX
  const regionCenterX = (left + right) / 2

  const rotatePoint = (x: number, y: number) => {
    const theta = (fabricImage.angle * Math.PI) / 180
    const cos = Math.cos(theta)
    const sin = Math.sin(theta)
    const dx = x - fabricImage.left
    const dy = y - fabricImage.top
    return {
      x: fabricImage.left + dx * cos - dy * sin,
      y: fabricImage.top + dx * sin + dy * cos,
    }
  }
  const leftPoint = fabricImage.left + (regionCenterX - fabricImage.width / 2) * fabricImage.scaleX
  let bottomPoint = { x: leftPoint, y: fabricImage.top }
  if (fabricImage.angle !== 0) {
    bottomPoint = rotatePoint(leftPoint, fabricImage.top + fabricImage.height * fabricImage.scaleY)
  }

  // Scale shadow to match the width of visible region
  const originalShadowHeight = shadowImg.getScaledHeight()
  shadowImg.scaleToWidth(scaledWidth)
  if(shadowShape == 'tilted') shadowImg.height = shadowImg.height * 1.5
  const minimumShadowHeight = 50
  if (shadowType === 'square')
  {
    const shadowHeightAfterScaling = shadowImg.getScaledHeight()
    const pixelsToIncrease = 15
    const finalShadowHeight = Math.min(shadowHeightAfterScaling + pixelsToIncrease, minimumShadowHeight)
    if (shadowHeightAfterScaling < minimumShadowHeight)
    {
      shadowImg.scaleY = finalShadowHeight / originalShadowHeight
    }
  }

  if (!(fabricImage as FabricImage & { layerId?: string }).layerId) {
    fabricImage.set('layerId', uuidv4())
  }

  shadowImg.set('layerId', (fabricImage as FabricImage & { layerId?: string }).layerId)
  if (fabricImage.originX === 'right') {
    bottomPoint.x = bottomPoint.x - fabricImage.getScaledWidth() / 2
  }
  if (fabricImage.originX === 'left') {
    bottomPoint.x = bottomPoint.x + fabricImage.getScaledWidth() / 2
  }
  shadowImg.set({
    left: shadowShape == 'tilted' ? bottomPoint.x - shadowImg.getScaledWidth() * 0.02 : bottomPoint.x,
    originX: 'center',
    originY: 'center',
    layerName: 'Shadow',
    opacity: shadowShape == 'tilted' ? 0.75 : 0.9,
  })

  if (shadowImageUrl == shadowImages.square[3]) {
    shadowImg.set({
      opacity: 0.75,
    })
  }

  shadowImg.set({ layerType: shadowType })
  shadowImg.set('globalCompositeOperation', 'multiply')

  return shadowImg
}

export async function createContactShadow(
  img: FabricImage,
  shadowType: string,
  canvas: Canvas,
  angle: number = 0,
): Promise<FabricImage> {
  const placementStore = usePlacementStore()
  let shadowUrl = shadowImages.ellipse[4] // product-middle

  if (angle != 0) {
    if (angle < 0) {
      shadowUrl = rotateShadowImages.left_thin // product-left
    } else if (angle > 0) {
      shadowUrl = rotateShadowImages.right_thin // product-right
    }
    ;(img as FabricImage & { shadowType?: string }).shadowType = shadowType
    return await addImageShadow(img, shadowUrl, shadowType, canvas)
  }
  const shape = getBottomShapeLabel(img)
  const {
    leftBottomWithoutRotation,
    centerBottomWithoutRotation,
    rightBottomWithoutRotation,
    centerBottom,
    leftBottom,
    rightBottom,
  } = getBottomCanvasPoints(img, shape)
  if (angle == 0) {
    const bounds = getLeftRightInBottomVisibleRegion(img, 0.05)
    const { left, right } = bounds
    let shadowWidth = 0
    if (left != null && right != null) {
      shadowWidth = (right - left) * img.scaleX
    }

    const irregularShaped = isIrregularShaped(img)

    if (shape == 'concave' && irregularShaped) {
      return await addImageShadow(
        img,
        shadowImages.ellipse[4], // product-middle
        'ellipse',
        canvas,
      )
    } else if (shadowWidth ? shadowWidth <= img.getScaledWidth() * 0.5 : false) {
      return await addImageShadow(
        img,
        shadowImages.ellipse[4], // product-middle
        'ellipse',
        canvas,
      )
    }
    else if(shape == 'tilted'){
      const shadow = await addImageShadow(
        img,
        shadowImages.square[19], // square/Dark-neutral-curved
        'square',
        canvas,
        'tilted'
      )
      shadow.top = rightBottom.y + shadow.getScaledHeight() * 0.2
      return shadow
    }
    else if (shape == 'pouch') {
      const shadow = await addImageShadow(
        img,
        shadowImages.square[17], // square/Dark-neutral-curved
        'square',
        canvas,
      )
      shadow.top = leftBottom.y + shadow.getScaledHeight() / 2
      return shadow
    } else if (shape == 'wavy') {
      const shadow = await addImageShadow(
        img,
        shadowImages.square[17], // square/Dark-neutral-curved
        'square',
        canvas,
      )
      shadow.top = leftBottom.y + shadow.getScaledHeight() / 2
      shadow.scaleX -= 0.02
      return shadow
    } else if (
      Math.abs(leftBottomWithoutRotation.y - centerBottomWithoutRotation.y) <=
        img.getScaledWidth() * 0.02 ||
      Math.abs(rightBottomWithoutRotation.y - centerBottomWithoutRotation.y) <=
        img.getScaledWidth() * 0.02
    ) {
      shadowType = 'square'
    } else if (shape == 'concave') {
      return await addImageShadow(
        img,
        shadowImages.ellipse[4], // product-middle
        'ellipse',
        canvas,
      )
    } else if (shape == 'convex') {
      return await addShadowForInvertedCurve(
        leftBottomWithoutRotation,
        rightBottomWithoutRotation,
        centerBottomWithoutRotation,
        centerBottom,
        img,
        canvas,
      )
    } else {
      shadowType = 'square'
    }
  }

  placementStore.setSelectedRotateIndex()
  if (shadowType == 'square') {
    shadowUrl = shadowImages.square[3] // Dark-neutral-curved
  }
  ;(img as FabricImage & { shadowType?: string }).shadowType = shadowType
  return await addImageShadow(img, shadowUrl, shadowType, canvas)
}

export function createDropShadow(target: FabricImage, direction: string): Shadow {
  if (
    direction !== DIRECTIONS.TOP_LEFT &&
    direction !== DIRECTIONS.TOP_RIGHT &&
    direction !== DIRECTIONS.BOTTOM_LEFT &&
    direction !== DIRECTIONS.BOTTOM
  ) {
    direction = DIRECTIONS.TOP_LEFT
  }
  const blur =
    direction == DIRECTIONS.BOTTOM
      ? MULTIPLE_PRODUCTS_DROP_SHADOW_BLUR
      : Math.max(5, Math.min(target.width * target.scaleX, target.height * target.scaleY) * 0.2)
  const offsetValX = Math.max(5, target.width * target.scaleX * 0.2)
  const offsetValY = Math.max(5, target.height * target.scaleY * 0.3)
  const offsetVal = Math.min(offsetValX, offsetValY)
  const offsetX =
    direction === DIRECTIONS.TOP_LEFT || direction === DIRECTIONS.BOTTOM_LEFT
      ? offsetVal
      : direction === DIRECTIONS.TOP_RIGHT
        ? -offsetVal
        : direction === DIRECTIONS.BOTTOM
          ? MULTIPLE_PRODUCTS_DROP_SHADOW_OFFSET_X
          : 0
  const offsetY =
    direction === DIRECTIONS.TOP_LEFT || direction === DIRECTIONS.TOP_RIGHT
      ? offsetVal
      : direction === DIRECTIONS.BOTTOM_LEFT
        ? -offsetVal
        : direction === DIRECTIONS.BOTTOM
          ? MULTIPLE_PRODUCTS_DROP_SHADOW_OFFSET_Y
          : 0
  const shadow = new Shadow({
    color:
      direction === DIRECTIONS.BOTTOM ? MULTIPLE_PRODUCTS_DROP_SHADOW_COLOR : DROP_SHADOW_COLOR,
    blur: blur,
    offsetX: offsetX,
    offsetY: offsetY,
  })
  return shadow
}

export function createAmbientShadow(target: FabricImage): Shadow {
  const blur = Math.min(target.width, target.height) * 0.75
  return new Shadow({
    color: AMBIENT_SHADOW_COLOR,
    blur: blur,
    offsetX: 0,
    offsetY: 0,
  })
}

export function getShadowColor(backgroundColor: string): string {
  const chromaColor = chroma(backgroundColor)
  const hue = chromaColor.get('hsl.h')
  const saturation = chromaColor.get('hsl.s')
  const lightness = chromaColor.get('hsl.l')

  const isYellowish = hue >= 40 && hue <= 70 && lightness > 0.3 && saturation > 0.3
  if (isYellowish) {
    return '#FFFFFF'
  }

  const argb = argbFromHex(backgroundColor)
  const palette = TonalPalette.fromInt(argb)
  const tone95 = hexFromArgb(palette.tone(95))

  return tone95
}

export const getBottomWaveCurvePoint = (fabricImage: any) => {
  const bottomPoints = getBottomContourEnhanced(fabricImage, 0.15)

  if (bottomPoints && bottomPoints.length) {
    const ignoreLeftRight = fabricImage.width * 0.20;

    const maxBottomPoint = Math.max(...bottomPoints.slice(ignoreLeftRight, -ignoreLeftRight).filter(p => p.hasPixel).map(p => p.y));
    const minBottomPoint = Math.min(...bottomPoints.slice(ignoreLeftRight, -ignoreLeftRight).filter(p => p.hasPixel).map(p => p.y));

    return (maxBottomPoint - minBottomPoint) * fabricImage.scaleY;
  }

  return 0;
}

function isWaveShape(points: number[]): boolean {
  const peaks: { index?: number; start?: number; end?: number; value: number }[] = [];
  const valleys: { index?: number; start?: number; end?: number; value: number }[] = [];

  let i = 1;
  while (i < points.length - 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    // Standard peak
    if (curr > prev && curr > next) {
      peaks.push({ index: i, value: curr });
      i++;
      continue;
    }

    // Standard valley
    if (curr < prev && curr < next) {
      valleys.push({ index: i, value: curr });
      i++;
      continue;
    }

    // Plateau detection
    if (curr === next) {
      const start = i;
      let end = i + 1;
      while (end < points.length && points[end] === curr) {
        end++;
      }

      const left = points[start - 1];
      const right = points[end];

      if (curr > left && curr > right) {
        peaks.push({ start, end: end - 1, value: curr });
      } else if (curr < left && curr < right) {
        valleys.push({ start, end: end - 1, value: curr });
      }

      i = end; // skip plateau
      continue;
    }

    i++;
  }

  // Merge peaks and valleys into one ordered list
  const extrema = [...peaks.map(p => ({ ...p, type: "peak" })), ...valleys.map(v => ({ ...v, type: "valley" }))];
  extrema.sort((a, b) => {
    const ai = a.index ?? a.start ?? 0;
    const bi = b.index ?? b.start ?? 0;
    return ai - bi;
  });

  let waveCount = 0;

  for (let j = 0; j < extrema.length - 1; j++) {
    const a = extrema[j];
    const b = extrema[j + 1];

    if (a.type === b.type) continue; // must alternate

    const diff = Math.abs(a.value - b.value);

    if (diff >= 10) {
      waveCount++;
      if (waveCount >= 3) return true;
    }
  }

  return false;
}

function isPouchShape(points: number[]): boolean {
  const flatThreshold = 12
  const curveTheshold = 45
  const count = Math.ceil(points.length * 0.10);

  const left = points.slice(0, count);
  const right = points.slice(-count);

  const leftFlatLength = longestContinuousSubarray(left);
  const rightFlatLength = longestContinuousSubarray(right);

  const middle = points.slice(count, points.length - count);

  const middleDiff = Math.min(Math.min(...left), Math.min(...right)) - Math.min(...middle)

  if (leftFlatLength >= flatThreshold && rightFlatLength >= flatThreshold
    && middleDiff <= curveTheshold
    && middleDiff >= 4)
  {
    return true
  }

  return false
}

function longestContinuousSubarray(arr: number[]) {
  let maxLen = 1;
  let currentLen = 1;
  let currentFlatValue = 0;

  for (let i = 1; i < arr.length; i++) {
    const diff = currentFlatValue - arr[i];
    if (diff < 1 && diff > -1) {
      currentLen++;
      maxLen = Math.max(maxLen, currentLen);
    } else {
      currentLen = 1; // reset when the difference >= 1
      currentFlatValue = arr[i]
    }
  }

  return maxLen;
}

function isFlatCurve(img: FabricImage) {
  let bottomPoints = getBottomContourEnhanced(img, 0.15)
  if (bottomPoints) {
    bottomPoints = bottomPoints.filter((pt) => pt.y != img.height)
    const yValues = bottomPoints.map((pt) => img.height - pt.y)

    const count = Math.ceil(yValues.length * 0.04);

    const middle = yValues.slice(count, yValues.length - count);

    for (let i = 5; i < middle.length; i++) {
      if (Math.abs(middle[i - 5] - middle[i]) > 5) {
        return false
      }
    }
  }

  return true
}

export function updateGlowShadow(fabricCanvas: Canvas) {
  const objects = fabricCanvas.getObjects()
  const productGroup = objects.find(
    (obj: any) => obj.type === 'group' && !obj.layerName.includes('CTA'),
  ) as Group
  if (productGroup) {
    productGroup.set('shadow', null)
    productGroup.set('shadow', createGlowShadow(productGroup))
    return
  }
  const productObjects = objects.filter((obj: any) =>
    obj.layerName.includes('Product'),
  ) as FabricImage[]
  if (productObjects.length == 1) {
    const productObject = productObjects[0]
    productObject.set('shadow', createGlowShadow(productObject))
  }
}

export function createGlowShadow(target: FabricImage | Group, color?: string) {
  const editorStore = useEditorStore()

  let shadowColor = '#FFFFFF'
  if (color) {
    shadowColor = color
  } else {
    const brandStore = useBrandStore()
    const bgColor = brandStore.selectedColor
    shadowColor = getShadowColor(bgColor)
  }
  const blur = Math.min(target.width * target.scaleX, target.height * target.scaleY) * 0.6

  if(!editorStore.isGlowShadowApplied){
      editorStore.setIsDropShadow(false)
      editorStore.setIsAiShadow(false)
      editorStore.setIsGlowShadowApplied(true)
      editorStore.setShadowLightingIndex(0)
      editorStore.setGlowColor(shadowColor)
  }

  editorStore.glowColor = shadowColor

  return new Shadow({
    color: shadowColor,
    blur: blur,
    offsetX: 0,
    offsetY: 0,
  })
}
